import { newE2EPage } from '@stencil/core/testing';

type TagOrHTML = keyof JSX.IntrinsicElements | string;

const isHTML = (tagOrHTML: string) => tagOrHTML.trim().startsWith('<');

/** Returns element tag from given string */
const getSelector = (tagOrHTML: string): string =>
  isHTML(tagOrHTML) ? tagOrHTML.trim().substring(1, tagOrHTML.search(/[>\s]/)) : tagOrHTML;

/** Tests if the component mounts into the page and displays as defaulted */
const renders = async (
  componentTagOrHTML: TagOrHTML,
  options: {
    display?: string;
    visible?: boolean;
  } = {}
): Promise<void> => {
  const html = isHTML(componentTagOrHTML) ? componentTagOrHTML : `<${componentTagOrHTML}></${componentTagOrHTML}>`;
  const page = await newE2EPage({ html });

  await page.waitForChanges();

  const element = await page.find(getSelector(componentTagOrHTML));

  expect(element).toHaveClass('hydrated');
  expect(await element.isVisible()).toBe(options.visible ?? true);
  expect((await element.getComputedStyle()).display).toBe(options.display ?? 'inline-flex');
};

interface CustomInputOptions extends HTMLInputElement {
  /** @param customValue is the text entered into the input field. */
  customValue: string;
}

const inputFormSubmit = async (
  options: Partial<CustomInputOptions> = {},
  componentName = 'abds-input'
): Promise<void> => {
  const page = await newE2EPage();
  const {
    checked = false,
    customValue = 'a',
    disabled = false,
    name = 'name',
    readOnly = false,
    required = false,
    value = '',
  } = options;

  await page.setContent(`
    <form>
      <${componentName} ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''} name='${name}' ${
    readOnly ? 'readonly' : ''
  } ${required ? 'required' : ''} value=${value ? value : ''}>label</${componentName}>
    </form>
  `);

  const componentElement = await page.find(componentName);

  if (componentName === 'abds-input') {
    await componentElement.click();
    await page.keyboard.type(customValue);
  } else {
    await page.keyboard.press('Tab');
  }

  await page.waitForChanges();
  await page.keyboard.press('Enter');
  await page.waitForChanges();

  const urlAfterSubmit = page.$$eval('body', () => window.location.href);

  await page.waitForChanges();

  if (componentName === 'abds-switch' || componentName === 'abds-checkbox' || componentName === 'abds-radio') {
    if (checked) {
      expect(await urlAfterSubmit).toContain(`${name}=${value}`);
    } else {
      expect(await urlAfterSubmit).not.toContain(`${name}=${value}`);
    }
    return;
  }

  if (readOnly && value) {
    expect(await urlAfterSubmit).toContain(`${name}=${value}`);
    return;
  }

  if (disabled) {
    expect(await urlAfterSubmit).not.toContain(`${name}=${customValue}`);
    return;
  }

  expect(await urlAfterSubmit).toContain(`${name}=${customValue}`);
};

export { renders, inputFormSubmit };
